import ScrollModal from "@/components/common/scroll-modal";
import {
  Heading,
  Button,
  Icon,
  GroupTextFieldRow,
  GroupTextField,
  Input,
  useToast,
  Avatar,
  AvatarFallback,
} from "@elaniintc/goes-design-system";
import { DialogTitle, DialogDescription } from "@/components/ui/dialog";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TextAreaField } from "@/components/common/text-field";
import {
  emailTemplateSchema,
  type EmailTemplateFormData,
} from "@/schemas/email-templates";
import { useCreateEmailTemplate } from "@/queries/templates/email-templates.api";
import { useNavigate } from "react-router";
import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";

interface NewEmailTemplateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const initialState: EmailTemplateFormData = {
  name: "",
  description: "",
};

export default function NewEmailTemplateModal({
  open,
  onClose,
  onSuccess,
}: NewEmailTemplateModalProps) {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
  } = useForm({
    resolver: zodResolver(emailTemplateSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialState,
  });
  const { mutateAsync, isPending: isLoadingCreate } = useCreateEmailTemplate();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { hasPermission } = useAuthorization();
  const canRead = hasPermission(Permissions["email-templates"].read);

  const onSubmit = async (data: EmailTemplateFormData) => {
    try {
      const response = await mutateAsync({
        name: data.name,
        description: data.description || undefined,
        subject: `Asunto: ${data.name}`,
        rich_text: `<p></p>`,
        status: "draft",
      });

      if (response.data.id && canRead) {
        navigate(`/plantillas/correos/${response.data.id}`);
      }

      showToast({
        title: "Guardado Satisfactorio",
        description: "La plantilla fue creada con éxito",
        options: {
          type: "success",
          duration: 5000,
          customIcon: (
            <Avatar size="xs" shape="square">
              <AvatarFallback color="action" shape="square" type="icon">
                <Icon type="fas" name="save" />
              </AvatarFallback>
            </Avatar>
          ),
        },
      });

      clearErrors();
      handleClose();
      onSuccess();
    } catch (e) {
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;

      if (error.response?.data.message?.includes("already exists")) {
        setError("name", {
          type: "server",
          message: "Ya existe una plantilla con este nombre",
        });
        return;
      }

      setError("name", {
        type: "server",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Error al crear la plantilla",
      });
    }
  };

  const handleClose = () => {
    reset({
      name: "",
      description: "",
    });
    onClose();
  };

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle className="sr-only">
            Nueva Plantilla de Email
          </DialogTitle>
          <DialogDescription className="sr-only">
            Formulario para crear una nueva plantilla de email
          </DialogDescription>
          <Heading variant="h5" className="!mb-0">
            Nueva Plantilla de Email
          </Heading>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="w-full cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            disabled={!isValid || isLoadingCreate}
            form="new-email-template-form"
            className="w-full cursor-pointer"
          >
            Crear Plantilla
            {isLoadingCreate && (
              <Icon type="fas" name="spinner-third" className="animate-spin" />
            )}
          </Button>
        </div>
      }
    >
      <form
        id="new-email-template-form"
        data-testid="new-email-template-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
      >
        {/* Contenido scrollable */}
        <div className="flex flex-col gap-6 pb-6">
          {/* Detalles */}
          <div>
            <Heading variant="h6" className="mb-4">
              Detalles
            </Heading>
            <div className="flex flex-col gap-4">
              <GroupTextField
                label="Nombre de la plantilla"
                required
                error={errors.name?.message || ""}
              >
                <GroupTextFieldRow>
                  <Input
                    className="w-full"
                    required
                    maxLength={255}
                    placeholder="Escribe un nombre para la plantilla"
                    {...register("name")}
                  />
                </GroupTextFieldRow>
              </GroupTextField>

              <TextAreaField
                label="Descripción"
                helperText="Opcional"
                maxLength={1000}
                placeholder="Describe el propósito de esta plantilla"
                rows={3}
                className="resize-none w-full"
                error={errors.description?.message || ""}
                {...register("description")}
              />
            </div>
          </div>
        </div>
      </form>
    </ScrollModal>
  );
}
